import busStopSelectedIcon from '../../../static/bus-stop-20-hover.png';
import busStopIcon from '../../../static/bus-stop-20.png';
import { VehiclePositionState } from '../../../types/busHistoryTypes';
import { DirectionVariant } from '../../../types/gtfsTypes';
import { Dictionary } from '../../../types/view-models-interfaces';
import { createVehicleInfobox } from '../../shared/VehicleMapElements';
import { AgencyModel } from './agencyModel';
import { BphDirectionsState, BphRoutesState, BphSliderState, DateTimeState, StopPredictionsState } from './bphStates';
import { trackColorOrdinary, trackColorSelected } from './Constants';
import { DateTime } from './dateTime';
import { PublicInstanceHelper } from './InternalInstanceHelper';
import { Optional } from './Optional';
import { SliderInfoProps as BphSliderInfoProps } from './SliderInfoProps';

export interface NamedEntity {
    displayName: string
}

export type BusHistoryUrlFilterKey = 'date' | 'time' | 'routeId' | 'stopId' | 'directionVariantId' | 'displayStops';

export interface BusHistoryUrlFilters {
    date?: string;
    time?: string;
    routeId?: string;
    stopId?: string;
    directionVariantId?: string;
    displayStops?: boolean;
}

export interface ParsedBusHistoryUrlFilters {
    filters: BusHistoryUrlFilters;
    hasSupportedParams: boolean;
    invalidKeys: BusHistoryUrlFilterKey[];
}

export type BusHistoryUrlApplyStage = 'idle' | 'dateTime' | 'route' | 'stop' | 'direction' | 'completed';

export interface BphUrlFiltersState {
    initialized: boolean;
    applying: boolean;
    stage: BusHistoryUrlApplyStage;
    sourceFilters: Nullable<BusHistoryUrlFilters>;
    pendingFilters: Nullable<BusHistoryUrlFilters>;
    droppedKeys: BusHistoryUrlFilterKey[];
}

export interface SecondsIntervalOptions {
    negativeSign?: string,
    positiveSign?: string,
    position?: 'before' | 'after'
}

export interface BusPositionHistoryStateType {
    helper: PublicInstanceHelper;
    mapReady: boolean;
    agency: AgencyModel | null;
    dateTime: DateTimeState;
    routesLoading: boolean;
    routesState: BphRoutesState;
    directionsLoading: boolean;
    directionsState: BphDirectionsState;
    directionsInfo: BphDirection[];
    stopsLoading: boolean;
    stopsState: BphStopsStateType;
    stopsSearchState: StopsSearchStateType;
    stopsNoResultMessageState?: string;
    stopsPredictions: Nullable<StopPredictionsState>;
    historyLoading: boolean;
    busPositionsState: VehiclePositionState[];
    scheduleDates: Nullable<DateTime[]>;
    sliderOptions: Nullable<BphSliderState>;
    sliderInfo: Nullable<BphSliderInfoProps>;
    displayDirectionStops: boolean;
    mapsBounds: Nullable<{ north: number; south: number; east: number; west: number; }>;
    vehicles: Nullable<BphVehicleModel[]>;
    urlFiltersState: BphUrlFiltersState;
}

export interface BphDirection {
    id: string;
    route: string;
    stop: string;
}

export interface BusPositionHistoryContextType extends BusPositionHistoryStateType {
    setAgency: (agency: AgencyModel | null) => void;
    initializeUrlFilters: (value?: Optional<ParsedBusHistoryUrlFilters>) => void;
    updateDateTime: (date: Optional<string>, time: Optional<string>) => boolean;
    selectRoute: (value?: Nullable<BphRouteModel>) => boolean;
    selectDirection: (value?: Optional<BphDirectionModel>) => boolean;
    selectStop(value?: Nullable<BphStopModel>): boolean;
    setStopsSearchString: (value?: Nullable<string>) => void;
    onSliderTimeChanged: (sliderValue: number) => void;
    onDisplayDirectionStopsChanged: (value: boolean) => void;
}

export type StopsSearchStateType = {
    searchQuery?: string | null;
};

export class BphRouteModel implements NamedEntity {
    constructor(
        public readonly routeId: string,
        public readonly displayName: string) {
    }
}

export class BphStopModel implements NamedEntity {
    public readonly pushpin: any;
    public readonly infobox: any;

    constructor(
        public readonly displayName: string,
        public readonly stopId: string,
        lat: number,
        lng: number,
        selected = true,
    ) {
        this.pushpin = {
            location: { latitude: lat, longitude: lng },
            options: { icon: selected ? busStopSelectedIcon : busStopIcon },
        };
        this.infobox = {
            location: { latitude: lat, longitude: lng },
            options: {
                description: `${displayName}`,
                visible: false,
            },
        };
    }

    public addPushpinHandler(_pushpin: any, _infoboxes: any[]) {
        // no-op with Azure map; handled elsewhere
    }
}

export class BphVehicleModel {
    private static _zero = { latitude: 0, longitude: 0 };
    public readonly id: string;
    public readonly infobox: any;
    private _description = '';
    private _highlighted = false;

    constructor(position: VehiclePositionState) {
        this.id = position.vehicleId;
        this.infobox = { location: BphVehicleModel._zero, options: {} };
        this.update(position);
    }

    public get description(): string {
        return this._description;
    }

    public get highlighted(): boolean {
        return this._highlighted;
    }

    public set highlighted(value: boolean) {
        this._highlighted = !!value;
        this._updateInfobox();
    }

    private _updateInfobox(): void {
        const c = this._highlighted ? '#c40000' : undefined;
        const infobox = createVehicleInfobox({
            location: [
                this.infobox.location.latitude as Latitude,
                this.infobox.location.longitude as Longitude,
            ],
            vehicleId: this.id,
            description: this._description,
            cssColor: c,
            popupVariant: 'html',
        });
        this.infobox.options = infobox.options;
    }

    public update(position: VehiclePositionState) {
        const location = { latitude: position.latitude, longitude: position.longitude };
        this.infobox.location = location;
        this._description = `at ${DateTime.formatAgencyDisplay(position.reportTimeAtz, 'h:mm:ss A')}`;
        this._updateInfobox();
    }
}

export class BphDirectionModel implements NamedEntity {
    public readonly directionVariantId: string;
    public readonly directionVariantInternalId: string;
    public readonly routeName: string;
    public readonly cardinalDirection: string;
    public readonly lastStop: string;
    public readonly polyline: any;
    public readonly bounds: { north: number; south: number; east: number; west: number };

    private readonly _stopIds?: Dictionary<BphStopModel>;

    constructor(
        public readonly displayName: string,
        source: DirectionVariant,
    ) {
        this.directionVariantId = source.directionVariantId;
        this.directionVariantInternalId = source.directionVariantInternalId;
        this.routeName = source.routeName;
        this.cardinalDirection = source.cardinalDirection;
        this.lastStop = source.lastStop;
        if (source.stops) {
            this._stopIds = {};
            for (const s of source.stops) {
                this._stopIds[s.stopId] = new BphStopModel(s.stopName, s.stopId, Number(s.lat), Number(s.lon), false);
            }
        }
        const lats = source.pointCoords.map(p => p[0] as number);
        const lons = source.pointCoords.map(p => p[1] as number);
        this.bounds = {
            north: Math.max(...lats),
            south: Math.min(...lats),
            east: Math.max(...lons),
            west: Math.min(...lons),
        };
        this.polyline = {
            locations: source.pointCoords.map(e => [e[0], e[1]]),
            options: { strokeColor: trackColorOrdinary, strokeThickness: 3 },
            setOptions: (opts: any) => { this.polyline.options = { ...this.polyline.options, ...opts }; },
        };
    }

    public get hasStops(): boolean {
        return !!this._stopIds;
    }

    public hasStop(stopId: string): boolean {
        return !!this._stopIds && (stopId in this._stopIds);
    }

    public select(selected: boolean) {
        this.polyline.setOptions({
            strokeColor: selected
                ? trackColorSelected
                : trackColorOrdinary,
        });
    }

    public getStops(): BphStopModel[] {
        return this._stopIds ? Object.values(this._stopIds) : [];
    }
}

export type BphStopsStateType = {
    stops: BphStopModel[] | null;
    selectedStop: BphStopModel | null;
    loadingSchedule: boolean;
};

export interface PreditionData {
    predictionTime: Nullable<string>;
    predictedTime: Nullable<string>;
    actualTime: Nullable<string>;
    scheduledTime: Nullable<string>;
}
